// ** React Imports
import { useState, useEffect, useRef, useMemo } from "react";

// ** MUI Components
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  FormLabel,
  MenuItem,
  Menu,
  Checkbox, 
  ListItemText, 
  ListSubheader,

} from "@mui/material";
import { useTheme } from "@mui/material";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { MobileTimePicker } from "@mui/x-date-pickers-pro";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup"; // or "@mui/lab/AvatarGroup" if your MUI version is <5.11
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
// ** Icon Imports
import Icon from "components/icon";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

// ** Toast Imports
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ** Custom Component Import
import CustomTextField from "components/mui/text-field";
import CustomChip from 'components/mui/chip';
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteWorkTimeLog } from "screens/Assessments/logic/GetWorkTimeLog"; // you ll need to implement this if you don t already have it

// ** Component Imports
import LineItems from "screens/Assessments/component/LineItems";
import dayjs from "dayjs";
import { saveJob, getJobLabors, getEmployees, getAllServiceWorkRequirement } from "../logic/WorkRequirementScheduleLogic";
import { useLineItemLogic } from "screens/Assessments/logic/line-item-logic";
import { copyBudgetedToActualsForJob } from "../logic/CopyJobsLogic";
import { useNavigate } from "react-router-dom";
import { GetJobAssets } from "screens/Assessments/logic/GetJobAsset";
import { GetJobLabors } from "screens/Assessments/logic/GetJobLabor";
import { GetJobMaterials } from "screens/Assessments/logic/GetJobMaterial";
import { GetJobMiscCosts } from "screens/Assessments/logic/GetJobMiscCost";
import { GetWorkTimeLog, SaveWorkTimeLog } from "screens/Assessments/logic/GetWorkTimeLog";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import AddTimeLog from "./AddTimeLog";
import { GetServiceRequestRaw, GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";
import { changeStatus } from "screens/Requests/logic/SchedulingLogic";
import { GetJobSubcontractor } from "screens/Assessments/logic/JobSubcontractor";
// import { GetJobPunches } from "../logic/GetJobPunches";



// const rowsTimeLog = [
//   {
//     id: 1,
//     employee: "John Doe",
//     checkIn: "2022-01-01 08:00:00",
//     checkOut: "2022-01-01 17:00:00",
//     timeSpent: "8hrs",
//     loggedBy: "System",
//   },
//   {
//     id: 2,
//     employee: "John Doe",
//     checkIn: "2022-01-01 08:00:00",
//     checkOut: "2022-01-01 17:00:00",
//     timeSpent: "8hrs",
//     loggedBy: "Crew Leader - John",
//   },
//   {
//     id: 3,
//     employee: "John Doe",
//     checkIn: "2022-01-01 08:00:00",
//     checkOut: "2022-01-01 17:00:00",
//     timeSpent: "8hrs",
//     loggedBy: "Crew Leader - John",
//   },
//   {
//     id: 4,
//     employee: "John Doe",
//     checkIn: "2022-01-01 08:00:00",
//     checkOut: "2022-01-01 17:00:00",
//     timeSpent: "8hrs",
//     loggedBy: "Crew Leader - John",
//   },
//   {
//     id: 5,
//     employee: "John Doe",
//     checkIn: "2022-01-01 08:00:00",
//     checkOut: "2022-01-01 17:00:00",
//     timeSpent: "8hrs",
//     loggedBy: "System",
//   },
// ];

const columnsBvASummary = [
  {
    flex: 0.1,
    field: "metric",
    minWidth: 180,
    headerName: "Metric",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.metric}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.1,
    field: "budgeted",
    minWidth: 120,
    headerName: "Budgeted",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.budgeted}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.1,
    field: "actual",
    minWidth: 120,
    headerName: "Actual",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.actual}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.1,
    field: "variance",
    minWidth: 180,
    headerName: "Variance",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.variance}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.1,
    field: "variancePer",
    minWidth: 140,
    headerName: "Variance (%)",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.variancePer}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.1,
    field: "status",
    headerName: "Status",
    minWidth: 120,
    renderCell: ({ row }) => {
      const { status } = row;

      return (
        <CustomChip
          rounded
          size="small"
          skin="light"
          sx={{ lineHeight: 1 }}
          color={
            status === "Under Budget"
              ? "success"
              : status === "Above Goal"
              ? "success"              
              : status === "Within Budget"
              ? "warning"
              : status === "Over Budget"
              ? "error"
              : status === "Below Goal"
              ? "error"              
              : "secondary"
          } //'success' // Use color names: primary, secondary, error, warning, info, success
          label={status}
        />
      );
    },
  },
];

const rowsBvASummary = [
  {
    id: 1,
    metric: "Total Cost",
    budgeted: "$10,000",
    actual: "$12,000",
    variance: "$2,000",
    variancePer: "20%",
    status: "Over Budget",
  },
  {
    id: 2,
    metric: "Total Price",
    budgeted: "$20,000",
    actual: "$25,000",
    variance: "$5,000",
    variancePer: "25%",
    status: "Within Budget",
  },
  {
    id: 3,
    metric: "Total Profit",
    budgeted: "$10,000",
    actual: "$12,000",
    variance: "$2,000",
    variancePer: "20%",
    status: "Under Budget",
  },
  {
    id: 4,
    metric: "Profit Margin",
    budgeted: "33.33%",
    actual: "25%",
    variance: "-8.33%",
    variancePer: "-",
    status: "Over Budget",
  },
];


const JobDetail = ({ 
  open,
  handleClose,
  method,
  entity,
  rules,
  workReq,
  job,
  setJob,
  setLoading,
  toast,
  fetchWorkRequirements,
  fetchRequests
}) => {
  const theme = useTheme();

  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate,   setEndDate]   = useState(null);
  const [endTime,   setEndTime]   = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [dateError, setDateError] = useState("");

// Assign To States
  const [tabValue, setTabValue] = useState("1");
  const [tabBvAValue, setTabBvAValue] = useState("1");
  
  const [timeLogDialogMode, setTimeLogDialogMode] = useState("add");
  const [editingLog, setEditingLog] = useState(null);
  const MySwal = withReactContent(Swal);

  // const didInitAssignees = useRef(false);

  const [jobStatus, setJobStatus] = useState(job?.status || "Unscheduled");
  const [actualKey, setActualKey] = useState(0);
  const [lineItemChanged, setLineItemChanged] = useState(false);
  const [serviceRequest, setServiceRequest] = useState({});
  // const [assignedEmployees, setAssignedEmployees] = useState([]);

  // const {
  //   LineItemProps,
  //   LineItemLaborProps,
  //   LineItemMaterialProps,
  //   LineItemMiscCostProps
  // } = useLineItemLogic({entity:"jobDetail", workReq, setLoading, toast, job});

  const CustomChipWithDropdown = ({ tag, onSelect, theme, allowedStatuses }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    
  
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
  
    const handleClose = () => {
      setAnchorEl(null);
    };
  
    const handleSelect = (selectedTag) => {
      onSelect(selectedTag);
      handleClose();
    };
  
    const statusColors = {
      "Not Viewed": "info",
      "Under Review": "info",
      "Completed": "success",
      "Submitted": "success",
      "On Hold": "warning",
      "Incomplete": "warning",
      "In Progress": "warning",
      "Ongoing": "warning",
      "Canceled": "error",
      "Closed": "error",
    };

    const statuses = allowedStatuses || ["Scheduled", "In Progress", "Ongoing", "Canceled", "On Hold", "Incomplete", "Completed"];


    return (
      <Box>
        <CustomChip
          rounded
          skin="light"
          sx={{ lineHeight: 1, "&:hover": { color: "#fff" } }}
          color={statusColors[tag] || "secondary"}
          icon={<FiberManualRecordIcon fontSize="small" />}
          label={tag}
          // onClick={handleClick}
        />
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} PaperProps={{ sx: { width: 200 } }}>
          {statuses.map((status) => {
            return (
              <MenuItem
                key={status}
                onClick={() => handleSelect(status)}
                // disabled={!rules.canEdit}
                disabled={job?.status ==="Canceled" || job?.status ==="Completed" }
              >
                {status}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    );
  };
  
  const getAllowedStatuses = (currentStatus) => {
    const lockedStatuses = ["Unscheduled", "Scheduled", "In Progress", "Ongoing", "Canceled", "On Hold", "Completed", "Incomplete"];
    if (lockedStatuses.includes(currentStatus)) {
      return lockedStatuses;
    }
    // Default statuses (add others if needed)
    return ["Unscheduled", "Scheduled", "In Progress", "Ongoing", "Canceled", "On Hold", "Completed", "Incomplete"];
  };

  const fetchRequestByID = async () => {
    try {
      setLoading(true);
      let RequestRaw = await GetServiceRequestRaw(0, workReq?.entityID);
      const newRequest = RequestRaw.result[0];
      setServiceRequest(newRequest);      
    } catch (error) {
      console.error("Error fetching Requests data:", error);
    } finally {
      setLoading(false);
    }
  };

  const budgetedLogic = useLineItemLogic({
    entity: "jobDetail",
    workReq,
    setLoading,
    toast,
    job
  });

  const actualLogic = useLineItemLogic({
    entity: "jobDetailActual",
    workReq,
    setLoading,
    toast,
    job
  });

  const bvaLogic = useLineItemLogic({
    entity: "jobDetailBvA",
    workReq,
    setLoading,
    toast,
    job
  });

  const {
    LineItemProps,
    LineItemObservationProps,
    LineItemMaterialProps,
    LineItemLaborProps,
    LineItemSubcontractorProps,
    LineItemMiscCostProps
  } = budgetedLogic;
  
  const {
    jobAssetList
  } = LineItemProps;

  const {
    jobLaborList, employeeList
  } = LineItemLaborProps;

  const {
    jobSubcontractorList, subcontractorList
  } = LineItemSubcontractorProps;

  const assignedEmployees = useMemo(() => {
    if (!jobLaborList.length || !employeeList.length) return []
    const ids = new Set(jobLaborList.map(l => l.employeeID).filter(Boolean))
    return employeeList.filter(e => ids.has(e.employeeID))
  }, [jobLaborList, employeeList])

  const {
    jobMaterialList
  } = LineItemMaterialProps;

  const {
    jobMiscCostList
  } = LineItemMiscCostProps;

  const { jobLaborList: actualJobLaborList } = actualLogic.LineItemLaborProps;

  const { jobSubcontractorList: actualJobSubcontractorList } = actualLogic.LineItemSubcontractorProps;

  const distinctEmployees = useMemo(() => {
    if (!actualJobLaborList.length || !employeeList.length) return []
    const ids = new Set(actualJobLaborList.map(l => l.employeeID).filter(Boolean))
    return employeeList.filter(e => ids.has(e.employeeID))
  }, [actualJobLaborList, employeeList])

    // new: hook the _actual_ job-detail entity so we can list actual labors
    // const { jobLaborList: actualJobLaborList } = useLineItemLogic({
    //   entity: "jobDetailActual",
    //   workReq, setLoading, toast, job
    // }).LineItemLaborProps;
    
  const [timeLogRows, setTimeLogRows] = useState([]);
  const [bvaSummaryRows, setBvaSummaryRows] = useState([]);
  const [bvaLoading, setBvaLoading] = useState(false);

  const navigate = useNavigate();
  const [timeLogDialogOpen, setTimeLogDialogOpen] = useState(false);
  // const [distinctEmployees, setDistinctEmployees] = useState([]);
  const [displayBanner, setDisplayBanner] = useState(true);
  const [jobPunches, setJobPunches] = useState({ checkIn: null, checkOut: null });
  const [isJobRunning, setIsJobRunning] = useState(false);
  const [jobStartTime, setJobStartTime] = useState(null);


  // Sync jobStatus whenever job or open changes
  useEffect(() => {
    if (open && job) setJobStatus(job.status || "Unscheduled");    

  }, [open, job]);

  useEffect (()=>{
    if(open && workReq?.entityID)
      fetchRequestByID();
  },[open && workReq?.entityID]);

  useEffect(() => {
    if (!open || !job) return;

    // schedule start/end setup using new separate date/time fields...
    if (job.scheduleStartDate) {
      setStartDate(dayjs(job.scheduleStartDate));
    } else {
      setStartDate(null);
    }
    
    if (job.scheduleStartTime) {
      // Parse time more safely
      const timeStr = job.scheduleStartTime;
      if (timeStr && timeStr.includes(':')) {
        const [hours, minutes, seconds] = timeStr.split(':');
        setStartTime(dayjs().hour(parseInt(hours, 10)).minute(parseInt(minutes, 10)).second(parseInt(seconds || 0, 10)));
      } else {
        setStartTime(null);
      }
    } else {
      setStartTime(null);
    }
    
    if (job.scheduleEndDate) {
      setEndDate(dayjs(job.scheduleEndDate));
    } else {
      setEndDate(null);
    }
    
    if (job.scheduleEndTime) {
      // Parse time more safely
      const timeStr = job.scheduleEndTime;
      if (timeStr && timeStr.includes(':')) {
        const [hours, minutes, seconds] = timeStr.split(':');
        setEndTime(dayjs().hour(parseInt(hours, 10)).minute(parseInt(minutes, 10)).second(parseInt(seconds || 0, 10)));
      } else {
        setEndTime(null);
      }
    } else {
      setEndTime(null);
    }

  }, [open, job, workReq]);

  useEffect(() => {
    if (open && job?.jobID) {
      
      (async () => {
        setLoading?.(true);
        try {
        const logs = await GetWorkTimeLog(job.jobID, navigate);
        console.log(job)
        const mapped = (logs || []).map(log => ({
          // Use the real primary key from the API
          id: log.workTimeLogID,
          workTimeLogID: log.workTimeLogID,
          // Preserve employeeID and human name
          employeeID: log.employeeID,
          employee: log.employeeName,
          checkIn: log.startDateTime,
          checkOut: log.endDateTime,
          timeSpent: log.duration,
          loggedBy: log.createdBy,
          
        }));

          const isCheckedIn = logs?.some((log) => log.startDateTime && !log.endDateTime);

          setIsJobRunning(isCheckedIn);

          setTimeLogRows(mapped);
          if (mapped.length) {
            // Compute min checkIn and max checkOut
            const { checkIn: firstIn, checkOut: firstOut } = mapped[0];
            const { checkIn: minCheckIn, checkOut: maxCheckOut } =
              mapped.slice(1).reduce(
                (acc, cur) => {
                  // ISO date strings compare lexicographically
                  if (cur.checkIn  < acc.checkIn)  acc.checkIn  = cur.checkIn;
                  if (cur.checkOut > acc.checkOut) acc.checkOut = cur.checkOut;
                  return acc;
                },
                { checkIn: firstIn, checkOut: firstOut }
              );
  
            setJobPunches({ checkIn: minCheckIn, checkOut: maxCheckOut });
            setJobStartTime(minCheckIn);
          } else {
            // no punches
            setJobPunches({ checkIn: null, checkOut: null });
          }
        } catch (e) {
          setTimeLogRows([]);
        } finally {
          setLoading?.(false);
        }
      })();
    }
  }, [open, job?.jobID]);


const getStartDateTime = () => {
  if (!startDate || !startTime) return null;
  return dayjs(startDate)
    .hour(dayjs(startTime).hour())
    .minute(dayjs(startTime).minute())
    .second(0);
};
 const handleStartDateChange = (newDate) => {
    setStartDate(newDate);
  };

  const handleStartTimeChange = (newTime) => {
    setStartTime(newTime);
  };

  const handleEndDateChange = (newDate) => {
    setEndDate(newDate);
  };

  const handleEndTimeChange = (newTime) => {
    setEndTime(newTime);
  };

const checkedInDate = useMemo(() => {
  if (!jobPunches?.checkIn) return null;
  const d = dayjs(jobPunches.checkIn);
  return d.isValid() ? d : null;
}, [jobPunches?.checkIn]);

const checkedOutDate = useMemo(() => {
  if (!jobPunches?.checkOut) return null;
  const d = dayjs(jobPunches.checkOut);
  return d.isValid() ? d : null;
}, [jobPunches?.checkOut]);

const getEndDateTime = () => {
  if (!endDate || !endTime) return null;
  return dayjs(endDate)
    .hour(dayjs(endTime).hour())
    .minute(dayjs(endTime).minute())
    .second(0);
};

const handleStartDateTimeChange = (newDateTime) => {
  if (newDateTime) {
    setStartDate(dayjs(newDateTime));
    setStartTime(dayjs(newDateTime));
  } else {
    setStartDate(null);
    setStartTime(null);
  }
};

const handleEndDateTimeChange = (newDateTime) => {
  if (newDateTime) {
    setEndDate(dayjs(newDateTime));
    setEndTime(dayjs(newDateTime));
  } else {
    setEndDate(null);
    setEndTime(null);
  }
};

  // Fetch and calculate BvA summary data
  useEffect(() => {
    if (open && job?.jobID) {
      (async () => {
        setBvaLoading(true);
        try {
          const [materials, labors, subcontractors, assets, miscs] = await Promise.all([
            GetJobMaterials(workReq?.workReqID || 0, 0, job.jobID, navigate),
            GetJobLabors(workReq?.workReqID || 0, 0, job.jobID, navigate),
            GetJobSubcontractor(workReq?.workReqID || 0, 0, job.jobID, navigate),
            GetJobAssets(workReq?.workReqID || 0, 0, job.jobID, navigate),
            GetJobMiscCosts(workReq?.workReqID || 0, 0, job.jobID, navigate),
          ]);
          // Calculate totals
          const sum = (arr, key) => arr.reduce((acc, x) => acc + (parseFloat(x[key]) || 0), 0);
          const budgetedCost = sum(materials, 'budgetedTotalCost') + sum(labors, 'budgetedTotalCost') + sum(subcontractors, 'budgetedTotalCost') + sum(assets, 'budgetedTotalCost') + sum(miscs, 'budgetedCostAmount');
          const actualCost = sum(materials, 'actualTotalCost') + sum(labors, 'actualTotalCost') + sum(subcontractors, 'budgetedTotalCost') + sum(assets, 'actualTotalCost') + sum(miscs, 'actualCostAmount');
          const budgetedPrice = sum(materials, 'budgetedTotalPrice') + sum(labors, 'budgetedTotalPrice') + sum(subcontractors, 'budgetedTotalCost') + sum(assets, 'budgetedTotalPrice') + sum(miscs, 'budgetedCustomerPrice');
          const actualPrice = sum(materials, 'actualTotalPrice') + sum(labors, 'actualTotalPrice') + sum(subcontractors, 'budgetedTotalCost') + sum(assets, 'actualTotalPrice') + sum(miscs, 'actualCustomerPrice');
          const budgetedProfit = budgetedPrice - budgetedCost;
          const actualProfit = actualPrice - actualCost;
          const budgetedMargin = (budgetedPrice && budgetedProfit) ? ((budgetedProfit / budgetedPrice) * 100).toFixed(2) + '%' : '-';
          const actualMargin = (actualPrice && actualProfit) ? ((actualProfit / actualPrice) * 100).toFixed(2) + '%' : '-';
          setBvaSummaryRows([
            {
              id: 1,
              metric: 'Total Cost',
              budgeted: budgetedCost ? `$${budgetedCost.toLocaleString()}` : '-',
              actual: actualCost ? `$${actualCost.toLocaleString()}` : '-',
              variance: (budgetedCost || actualCost) ? `$${(actualCost - budgetedCost).toLocaleString()}` : '-',
              variancePer: budgetedCost ? `${(((actualCost - budgetedCost) / budgetedCost) * 100).toFixed(2)}%` : '-',
              status: actualCost > budgetedCost ? 'Over Budget' : actualCost < budgetedCost ? 'Under Budget' : 'Within Budget',
            },
            {
              id: 2,
              metric: 'Total Price',
              budgeted: budgetedPrice ? `$${budgetedPrice.toLocaleString()}` : '-',
              actual: actualPrice ? `$${actualPrice.toLocaleString()}` : '-',
              variance: (budgetedPrice || actualPrice) ? `$${(actualPrice - budgetedPrice).toLocaleString()}` : '-',
              variancePer: budgetedPrice ? `${(((actualPrice - budgetedPrice) / budgetedPrice) * 100).toFixed(2)}%` : '-',
              status: actualPrice > budgetedPrice ? 'Over Budget' : actualPrice < budgetedPrice ? 'Under Budget' : 'Within Budget',
            },
            {
              id: 3,
              metric: 'Total Profit',
              budgeted: (budgetedPrice || budgetedCost) ? `$${budgetedProfit.toLocaleString()}` : '-',
              actual: (actualPrice || actualCost) ? `$${actualProfit.toLocaleString()}` : '-',
              variance: (budgetedProfit || actualProfit) ? `$${(actualProfit - budgetedProfit).toLocaleString()}` : '-',
              variancePer: budgetedProfit ? `${(((actualProfit - budgetedProfit) / budgetedProfit) * 100).toFixed(2)}%` : '-',
              status: (actualProfit > budgetedProfit) ? 'Above Goal' : (actualProfit < budgetedProfit) ? 'Below Goal' : 'Within Goal',
            },
            {
              id: 4,
              metric: 'Profit Margin',
              budgeted: budgetedMargin,
              actual: actualMargin,
              variance: '-',
              variancePer: '-',
              status: (actualMargin !== '-' && budgetedMargin !== '-') ? (parseFloat(actualMargin) > parseFloat(budgetedMargin) ? 'Above Goal' : parseFloat(actualMargin) < parseFloat(budgetedMargin) ? 'Below Goal' : 'Within Goal') : '-',
            },
          ]);
        } catch (e) {
          setBvaSummaryRows([]);
        } finally {
          setBvaLoading(false);
        }
      })();
    }
  }, [open, job?.jobID, workReq?.workReqID]);


  const handleEditTimeLog = (row) => {
    setEditingLog({
      workTimeLogID: row.workTimeLogID,
      employeeID: row.employeeID,
      checkIn: dayjs(row.checkIn),
      checkOut: dayjs(row.checkOut),
      
    });
    setTimeLogDialogMode("edit");
    setTimeLogDialogOpen(true);
  };
  
  const handleDeleteTimeLog = async (row) => {
    if (!row.workTimeLogID) return;
    setLoading?.(true);
    try {
      await DeleteWorkTimeLog(row.workTimeLogID, navigate);
      toast.success("Time log deleted");
      handleTimeLogSuccess();  // to refresh grid
    } catch {
      toast.error("Failed to delete");
    } finally {
      setLoading?.(false);
    }
  };
    
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleTabBvAChange = (event, newValue) => {
    setTabBvAValue(newValue);
  };
const handleEndJobClick = () => {
  MySwal.fire({
    title: '<strong>End Job</strong>',
    html: '<span>How would you like to mark this job?</span>',
    showCancelButton: true,
    confirmButtonText: 'Complete Job',
    cancelButtonText: 'Mark as Ongoing',
    width: '390px',
    padding: '1rem',
    focusConfirm: false,
    showCloseButton: true,
    closeButtonHtml: '&times;',
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
      title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
      htmlContainer: 'custom-swal-html',
      confirmButton: 'custom-swal-confirm',
      cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
      closeButton: 'custom-swal-close'
    },
    }).then((result) => {
    if (result.isConfirmed) {
      handleEndJobConfirm("Completed");
    } else if (result.dismiss === MySwal.DismissReason.cancel) {
      handleEndJobConfirm("Ongoing");
    }
  });
};

  const handleStartJob = async () => {
  if (!job || !jobLaborList?.length) {
    toast.error("No labors assigned to this job.");
    return;
  }

  // Disable start if job is completed
  if (jobStatus === "Completed") {
    toast.error("Cannot start a completed job.");
    return;
  }

  const startTime = new Date().toISOString();
  setJobStartTime(startTime);
  setIsJobRunning(true);
  setLoading(true);

  try {
    // Create a SINGLE time log entry for the job start
    await SaveWorkTimeLog({
      workTimeLogID: 0,
      jobID: job.jobID,
      employeeID: jobLaborList[0].employeeID, // Use first employee as primary
      startDateTime: startTime,
      endDateTime: null, 
      duration: 0,
      breakMinutes: 0,
      laborType: "Regular",
      status: "InProgress",
      notes: "Job started",
    }, navigate);

    toast.success("Job started!");
    handleTimeLogSuccess();
    await handleStatusChange("In Progress");
  } catch (error) {
    console.error(error);
    toast.error("Failed to start job.");
  } finally {
    setLoading(false);
  }
};

const handleEndJobConfirm = async (endStatus) => {
  if (!isJobRunning || !jobStartTime) {
    toast.error("No active job timer found.");
    return;
  }

  const endTime = new Date().toISOString();
  setIsJobRunning(false);
  setLoading(true);

  try {
    // Find the active time log that was created when job started
    const logs = await GetWorkTimeLog(job.jobID, navigate);
    const activeLog = logs?.find(log => 
      log.endDateTime === null && log.status === "InProgress"
    );

    if (!activeLog) {
      toast.error("No active time log found to update.");
      return;
    }

    // Calculate duration
    const startMs = new Date(activeLog.startDateTime).getTime();
    const endMs = new Date(endTime).getTime();
    const durationMs = endMs - startMs;
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    await SaveWorkTimeLog({
      workTimeLogID: activeLog.workTimeLogID, 
      jobID: job.jobID,
      employeeID: activeLog.employeeID,
      startDateTime: activeLog.startDateTime, 
      // endDateTime: endStatus === "Completed" ? endTime : null, 
      endDateTime: endTime, 
      duration: endStatus === "Completed" ? durationMinutes : 0,
      breakMinutes: 0,
      laborType: activeLog.laborType || "Regular",
      status: endStatus === "Completed" ? "Submitted" : "InProgress",
      notes: endStatus === "Completed" ? "Job completed" : "Job ongoing",
    }, navigate);

    toast.success(`Job ${endStatus === "Completed" ? "completed" : "marked as ongoing"}!`);
    handleTimeLogSuccess();
    await handleStatusChange(endStatus);
  } catch (error) {
    console.error(error);
    toast.error("Failed to end job.");
  } finally {
    setLoading(false);
  }
};



  const TabItems = () => [
    <Tab
      key="1"
      value="1"
      label={
         <Tooltip title="The materials,Assets and Employees that were estimated to be utilized" arrow>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
            color: theme.palette.mode === 'dark' ? 
              tabValue === "1" ? "#fff" : "inherit" :
              "inherit"
          }}
        >
          <Box
            sx={{
              fontSize: { xs: '0.875rem', md: "1rem" },
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            Budgeted Resources
          </Box>
        </Box>
        </Tooltip>
      }
    />,
    ...(method !== 'create' ? [
      <Tab
        key="2"
        value="2"
        label={
          <Tooltip title="The materials,Assets and Employees that were actually utilized" arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
              color: theme.palette.mode === 'dark' ? 
                tabValue === "2" ? "#fff" : "inherit" :
                "inherit"
            }}
          >
            <Box
              sx={{
                fontSize: { xs: '0.875rem', md: "1rem" },
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              Actual Resources Used
            </Box>
          </Box>
          </Tooltip>
        }
      />,
      <Tab
        key="3"
        value="3"
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
              color: theme.palette.mode === 'dark' ? 
                tabValue === "3" ? "#fff" : "inherit" :
                "inherit"
            }}
          >
            <Box
              sx={{
                fontSize: { xs: '0.875rem', md: "1rem" },
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              Time Log
            </Box>
          </Box>
        }
      />,
      <Tab
        key="4"
        value="4"
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
              color: theme.palette.mode === 'dark' ? 
                tabValue === "4" ? "#fff" : "inherit" :
                "inherit"
            }}
          >
            <Box
              sx={{
                fontSize: { xs: '0.875rem', md: "1rem" },
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              Budget vs Actual
            </Box>
          </Box>
        }
      />
    ] : [])
  ];

    // Build & save the updated job
    const handleSaveJob = async () => {
      // console.log(job)
      // console.log(workReq)
      if (!job || !workReq) return;

      if (startDate && startTime && endDate && endTime) {
        const startDT = dayjs(startDate)
          .hour(startTime.hour())
          .minute(startTime.minute());
        const endDT = dayjs(endDate)
          .hour(endTime.hour())
          .minute(endTime.minute());
    
        if (startDT.isAfter(endDT)) {
          // setDateError("Start date/time must be before end date/time");
          toast.error("Start date/time must be before End date/time");

          return;  // abort save
        }
      }
  
      // prepare separate date & time fields for new API format
    let scheduleStartDate = null;
    let scheduleStartTime = null;
    let scheduleEndDate = null;
    let scheduleEndTime = null;
    
    if (startDate) {
      scheduleStartDate = dayjs(startDate).format("YYYY-MM-DD");
    }
    if (startTime && dayjs(startTime).isValid()) {
      scheduleStartTime = dayjs(startTime).format("HH:mm:ss");
    }
    if (endDate) {
      scheduleEndDate = dayjs(endDate).format("YYYY-MM-DD");
    }
    if (endTime && dayjs(endTime).isValid()) {
      scheduleEndTime = dayjs(endTime).format("HH:mm:ss");
    }
    
    // Debug: Log the formatted values
    // console.log('JobDetail Save Debug:', {
    //   scheduleStartDate,
    //   scheduleStartTime,
    //   scheduleEndDate,
    //   scheduleEndTime,
    //   startTime: startTime ? startTime.format('HH:mm:ss') : null,
    //   endTime: endTime ? endTime.format('HH:mm:ss') : null
    // });
    
    // Determine job status - ALL 4 fields must be present for Scheduled status
    const hasCompleteSchedule = scheduleStartDate && scheduleStartTime && scheduleEndDate && scheduleEndTime;
    // const jobStatus = hasCompleteSchedule ? "Scheduled" : "unscheduled";
    console.log(job.status)
    const jobStatus = hasCompleteSchedule && job.status == "unscheduled" ? "Scheduled" : job.status;
console.log(jobStatus)
      // base payload
      // console.log(hasCompleteSchedule)
      // console.log(jobStatus)
      const payload = {
        jobID:         job.jobID,
        workReqID:     workReq.workReqID,
        flatRate:      job.flatRate,
        billingType:   job.billingType,
        occurrenceNo:  job.occurrenceNo,
        scheduleStartDate,
        scheduleStartTime,
        scheduleEndDate,
        scheduleEndTime,
        assignees:     assignees.length > 0 ? assignees : null,
        status: jobStatus,
      };

      // carry through any other existing non-null fields
      ['scheduleMethod','reminder','reminderTime']
        .forEach(f => {
          if (job?.[f] != null) payload[f] = job?.[f];
        });
        
      // console.log('Complete JobDetail Payload:', payload);
  
      try {
        
        await saveJob(payload, navigate);
        toast.success('Job updated');
        // re-fetch your WRs (to refresh the "From:/Until:" display)
        
        fetchRequests(workReq.entityID, true)
        handleClose();
        fetchWorkRequirements(workReq.entityID);
      } catch (e) {
        // already handled in saveJob
      }
    };

    const handleCloseJob = () => {
      handleClose();
      if(workReq?.entityID){
        fetchRequests(workReq?.entityID, true);
      }      
    }
    

    const handleStatusChange = async (newStatus) => {
      if (!job || !workReq) return;

        if (jobStatus === "Scheduled" && newStatus === "Unscheduled") {
          toast.error("Status cannot be changed from Scheduled to Unscheduled");
          return;
        }
      if (newStatus === "Canceled" && (jobStatus === "In Progress" || jobStatus === "Completed")) {
        toast.error("Cannot cancel job at this stage.");
        return;
      }
    
      // Save status update
      setLoading(true);
      let statusSaved = false;

      try {
        const res = await saveJob({
          jobID: job.jobID,
          workReqID: workReq.workReqID,
          status: newStatus,
        }, navigate);
        console.log(res)
      
        if (res && !res.isError) {
          setJobStatus(newStatus);
          setJob(prev => ({ ...prev, status: newStatus }));
      
          const [sr] = await GetServiceRequests(0, workReq?.entityID);
      
          const allServices = await getAllServiceWorkRequirement(workReq?.entityID);

          const occJobs = (allServices ?? [])
            .flatMap(svc => svc.jobs ?? [])
            .filter(j => j.occurrenceNo > 0);

          // Use the new status for the job you just saved
          const statuses = occJobs.map(j => (j.jobID === job.jobID ? newStatus : j.status));

          // helpers
          const isClosed = s => s === 'Completed' || s === 'Canceled' || s === 'Cancelled';

          let target = sr?.status;

          // Priority 1: any job in progress
          if (statuses.includes('In Progress')) {
            target = 'In Progress';
          } 
          // Priority 2: no job in progress and at least one on hold
          else if (statuses.includes('On Hold')) {
            target = 'On Hold';
          } 
          // Priority 3: all closed -> Completed if any completed else Canceled
          else {
            // const allClosed = statuses.length > 0 && statuses.every(isClosed);
            // if (allClosed) {
            //   target = statuses.includes('Completed') ? 'Completed' : 'Canceled';
            // }
          }

          // Apply only if it s a change
          if (target && target !== sr?.status) {
            await changeStatus(workReq.entityID, target);
          }

      
          toast.success(`Status updated to "${newStatus}"`);
          statusSaved = true;
      
          // Copy budgeted ? actual when a job starts
          if (jobStatus !== 'In Progress' && newStatus === 'In Progress') {
            const jobAssetList = await GetJobAssets(workReq.workReqID, 0, job.jobID, navigate) || [];
            const jobLaborList = await GetJobLabors(workReq.workReqID, 0, job.jobID, navigate) || [];
            const jobSubcontractorList = await GetJobSubcontractor(workReq.workReqID, 0, job.jobID, navigate) || [];
            const jobMaterialList = await GetJobMaterials(workReq.workReqID, 0, job.jobID, navigate) || [];
            const jobMiscCostList = await GetJobMiscCosts(workReq.workReqID, 0, job.jobID, navigate) || [];
            await copyBudgetedToActualsForJob({ jobAssetList, jobLaborList, jobSubcontractorList, jobMaterialList, jobMiscCostList, setLoading, toast, navigate });
          }
      
          fetchWorkRequirements(workReq.entityID);
        }
      } catch (err) {
        toast.error('Failed to update status');
      } finally {
        setLoading(false);
      }
      
      
      if (statusSaved && newStatus === "In Progress") {
        // run each fetch, but don t blow up your status call if one fails
        await Promise.allSettled([
          actualLogic.LineItemProps.fetchJobAssets(workReq.workReqID),
          actualLogic.LineItemLaborProps.fetchJobLabors(workReq.workReqID),
          actualLogic.LineItemSubcontractorProps.fetchJobSubcontractors(workReq.workReqID),
          actualLogic.LineItemMaterialProps.fetchJobMaterials(workReq.workReqID),
          actualLogic.LineItemMiscCostProps.fetchJobMiscCosts(workReq.workReqID),
        ]);
      }

    };

  // Determine if editing should be disabled for Budgeted Resources
  const disableBudgetedEdit = ["In Progress", "Canceled", "Completed"].includes(jobStatus);

  // Add this function to handle time log dialog
  const handleTimeLogDialogClose = () => {
    setTimeLogDialogOpen(false);
  };

  const handleTimeLogSuccess = () => {
    // Refresh time logs
    if (open && job?.jobID) {
      (async () => {
        setLoading?.(true);
        try {
          const logs = await GetWorkTimeLog(job.jobID, navigate);
          const mapped = (logs || []).map((log, idx) => ({
            id: log.workTimeLogID,
            workTimeLogID: log.workTimeLogID,
            employee: log.employeeName || log.employee || '',
            checkIn: log.startDateTime || '',
            checkOut: log.endDateTime || '',
            timeSpent: log.duration || '',
            loggedBy: log.createdBy || '',
          }));
          setTimeLogRows(mapped);
          if (mapped.length) {
            // Compute min checkIn and max checkOut
            const { checkIn: firstIn, checkOut: firstOut } = mapped[0];
            const { checkIn: minCheckIn, checkOut: maxCheckOut } =
              mapped.slice(1).reduce(
                (acc, cur) => {
                  // ISO date strings compare lexicographically
                  if (cur.checkIn  < acc.checkIn)  acc.checkIn  = cur.checkIn;
                  if (cur.checkOut > acc.checkOut) acc.checkOut = cur.checkOut;
                  return acc;
                },
                { checkIn: firstIn, checkOut: firstOut }
              );
  
            setJobPunches({ checkIn: minCheckIn, checkOut: maxCheckOut });
          } else {
            // no punches
            setJobPunches({ checkIn: null, checkOut: null });
          }
        } catch (e) {
          setTimeLogRows([]);
        } finally {
          setLoading?.(false);
        }
      })();
    }
  };
  
  const totalOccurrences = useMemo(() => {
    if (workReq?.jobs && workReq.jobs.length > 1) {
      return workReq.jobs.length - 1;
    }
    return 0;
  }, [workReq?.jobs]);

  const formatDuration = (minutes) => {
    if (isNaN(minutes) || minutes === null || minutes === undefined) {
      return "0m";
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes}min`;
    } else if (remainingMinutes === 0) {
      return `${hours}hr`;
    } else {
      return `${hours}hr ${remainingMinutes}min`;
    }
  };

  const columnsTimeLog = [
  {
    flex: 0.125,
    field: "employee",
    minWidth: 180,
    headerName: "Employee",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.employee}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.15,
    field: "checkIn",
    minWidth: 120,
    headerName: "Start Time",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.checkIn ? dayjs(row.checkIn).format("MM/DD/YYYY HH:mm:ss") : ''}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.15,
    field: "checkOut",
    minWidth: 120,
    headerName: "End Time",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.checkOut ? dayjs(row.checkOut).format("MM/DD/YYYY HH:mm:ss") : ''}</Typography>
      </Box>
    ),
  },
  {
    flex: 0.125,
    field: "timeSpent",
    minWidth: 180,
    headerName: "Total Time Spent",
    renderCell: ({ row }) => {
      return(
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatDuration(row.timeSpent || 0)}</Typography>
        </Box>
      )
    }
  },
  {
    flex: 0.15,
    field: "loggedBy",
    minWidth: 140,
    headerName: "Logged By",
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography>{row.loggedBy}</Typography>
      </Box>
    ),
  },
  {
    field: "actions",
    type: "actions",
    headerName: "Actions",
    width: 100,
    getActions: ({ row }) => [
      <GridActionsCellItem
        icon={<EditIcon />}
        label="Edit"
        onClick={() => handleEditTimeLog(row)}
        showInMenu={false}
      />,
      <GridActionsCellItem
        icon={<DeleteIcon />}
        label="Delete"
        onClick={() => handleDeleteTimeLog(row)}
        showInMenu={false}
      />
    ]
  }
];

const originalStart = job?.scheduleStartDate
  ? (() => {
      let d = dayjs(job.scheduleStartDate);
      if (job.scheduleStartTime) {
        const time = dayjs(`1970-01-01T${job.scheduleStartTime}`);
        d = d.hour(time.hour()).minute(time.minute());
      }
      return d;
    })()
  : dayjs(); 

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xl"
        open={open}
        onClose={handleCloseJob}
        PaperProps={{
          sx: {
            height: "100%",
            minWidth: "90vw",
          },
        }}
      >
                <DialogTitle
            sx={{
              display: "flex",
              flexDirection: "column",
              borderBottom: "1px solid #D9D9D9",
              paddingBottom: "20px !important",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h4" component="h2">
                  {workReq?.industryName} - {workReq && workReq.subServiceName}
                </Typography>
                <Typography variant="body1" fontWeight={600} color="#23A26D">
                  {job && (
                    totalOccurrences > 1
                      ? `Job ${job.occurrenceNo} of ${totalOccurrences}`
                      : `Job - ${job.occurrenceNo}`
                  )}
                </Typography>
              </Box>
              <Tooltip title="Close">
                <IconButton onClick={handleCloseJob}>
                  <Icon icon="tabler:x" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
              pr: 6, 
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
              {serviceRequest?.jobSiteName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon icon="tabler:building" />
                  <Typography variant="body1">
                    {serviceRequest.jobSiteName}
                  </Typography>
                </Box>
              )}
              {serviceRequest?.customerName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon icon="prime:user" />
                  <Typography variant="body1">
                    {serviceRequest.customerName}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                mr: 5, 
              }}
            >
               <Button
                variant="contained"
                color="main"
                disabled={!jobLaborList?.length || isJobRunning || jobStatus === "Completed"}
                onClick={handleStartJob}
                startIcon={<Icon icon="gravity-ui:clock" />} 
              >
                Start Job
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={!jobLaborList?.length || !isJobRunning || jobStatus === "Completed"}
                onClick={handleEndJobClick}
              >
                End Job
              </Button>
            </Box>
          </Box>
          </DialogTitle>


        <DialogContent>
          <TabContext value={tabValue}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* Full-width TabList */}
              <TabList
                onChange={handleTabChange}
                aria-label="simple tabs example"
                scrollButtons="false"
                variant="scrollable"
                sx={{ flex: 1 }}
              >
                {TabItems()}
              </TabList>

              {/* Status absolutely aligned on the right, without cutting tab underline */}
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  mb: 4,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                
                <CustomChipWithDropdown
                  tag={jobStatus}
                  onSelect={async (selectedTag) => {
                    if (selectedTag === jobStatus) return;
                    await handleStatusChange(selectedTag);
                  }}
                  theme={theme}
                  allowedStatuses={getAllowedStatuses(jobStatus)}
                   disabled={true} 
                   readOnly={true}
                />
              </Box>
            </Box>
            <TabPanel value="1" sx={{ px: { xs: 0, md: 2 } }}>
              <Grid container spacing={1}>
                <Box
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    mb: 4,
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2}>
                      {/* Start Date */}
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          Start Date
                        </FormLabel>
                        <DatePicker
                          value={startDate}
                          onChange={handleStartDateChange}
                          minDate={dayjs()}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Grid>

                      {/* Start Time */}
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          Start Time
                        </FormLabel>
                        <MobileTimePicker
                          value={startTime}
                          onChange={handleStartTimeChange}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Grid>

                      {/* End Date */}
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          End Date
                        </FormLabel>
                        <DatePicker
                          value={endDate}
                          onChange={handleEndDateChange}
                          minDate={startDate || dayjs()}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Grid>

                      {/* End Time */}
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          End Time
                        </FormLabel>
                        <MobileTimePicker
                          value={endTime}
                          onChange={handleEndTimeChange}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Grid>

                      {/* Assignees */}
                      {/* <Grid item xs={12} sm={2}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            marginBottom: "4px",
                            color: "text.primary",
                          }}
                        >
                          Assignees
                        </FormLabel>
                        <AvatarGroup
                          max={4}
                          className="pull-up"
                          sx={{
                            "& .MuiAvatar-root": { width: 25, height: 25 },
                            "& .MuiAvatarGroup-avatar": {
                              width: 25,
                              height: 25,
                            },
                          }}
                        >
                          {assignedEmployees.map((employee, idx) => {
                            const name = `${employee.firstName} ${employee.lastName}`;
                            return (
                              <Tooltip key={employee.employeeID} title={name}>
                                <Avatar
                                  src={
                                    employee.avatarUrl ||
                                    `/images/avatars/${(idx % 6) + 1}.png`
                                  }
                                  alt={name}
                                  sx={{ width: 25, height: 25 }}
                                />
                              </Tooltip>
                            );
                          })}
                        </AvatarGroup>
                      </Grid> */}
                    </Grid>
                  </LocalizationProvider>
                </Box>

                {/* {dateError && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="caption">
                      {dateError}
                    </Typography>
                  </Grid>
                )} */}
                {/* <Grid item xs={12}>
                  <LineItems entity='workOrder' rules={rules} workReq={workReq} setLoading={setLoading} toast={toast}/>
                </Grid> */}
                <Grid item xs={12}>
                  <LineItems
                    entity="jobDetail"
                    job={job} // <-- Pass the current job object!
                    workReq={workReq}
                    rules={rules}
                    setLoading={setLoading}
                    toast={toast}
                    disableEdit={disableBudgetedEdit}
                    LineItemProps={budgetedLogic.LineItemProps}
                    LineItemObservationProps={
                      budgetedLogic.LineItemObservationProps
                    }
                    LineItemMaterialProps={budgetedLogic.LineItemMaterialProps}
                    LineItemLaborProps={budgetedLogic.LineItemLaborProps}
                    LineItemSubcontractorProps={budgetedLogic.LineItemSubcontractorProps}
                    LineItemMiscCostProps={budgetedLogic.LineItemMiscCostProps}
                    setLineItemChanged={setLineItemChanged}
                  />
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value="2" sx={{ px: { xs: 0, md: 2 } }}>
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  {displayBanner && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          backgroundColor: "rgb(35, 162, 109, 0.1)",
                          border: "1px solid",
                          borderColor: "success.main",
                          borderRadius: "12px",
                          py: 1,
                          px: 3,
                          gap: 1,
                          mb: 3,
                        }}
                      >
                        <Icon icon="tabler:info-circle" />
                        <Typography
                          variant="h6"
                          fontWeight="400"
                          sx={{ ml: 2, mr: 3 }}
                        >
                          This data is updated when the job starts and
                          progresses.
                        </Typography>
                        <IconButton onClick={() => setDisplayBanner(false)}>
                          <Icon icon="tabler:x" />
                        </IconButton>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Box
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    mb: 4,
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2}>
                      {/* Start Date */}
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          Checked In Date
                        </FormLabel>
                        <DatePicker
                          value={checkedInDate}
                          readOnly
                          disableOpenPicker
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          Checked In Time
                        </FormLabel>
                        <MobileTimePicker
                          value={checkedInDate}
                          readOnly
                          disableOpenPicker
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </Grid>

                      {/* Checked Out */}
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          Checked Out Date
                        </FormLabel>
                        <DatePicker
                          value={checkedOutDate}
                          readOnly
                          disableOpenPicker
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            mb: "5px",
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          Checked Out Date
                        </FormLabel>
                        <MobileTimePicker
                          value={checkedOutDate}
                          readOnly
                          disableOpenPicker
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </Box>

                <Grid item xs={12}>
                  <LineItems
                    entity="jobDetailActual"
                    job={job}
                    workReq={workReq}
                    rules={rules}
                    setLoading={setLoading}
                    toast={toast}
                    forceEmpty={
                      !(
                        jobStatus === "In Progress" || jobStatus === "Completed"
                      )
                    }
                    LineItemProps={actualLogic.LineItemProps}
                    LineItemObservationProps={
                      actualLogic.LineItemObservationProps
                    }
                    LineItemMaterialProps={actualLogic.LineItemMaterialProps}
                    LineItemLaborProps={actualLogic.LineItemLaborProps}
                    LineItemSubcontractorProps={actualLogic.LineItemSubcontractorProps}
                    LineItemMiscCostProps={actualLogic.LineItemMiscCostProps}
                    setLineItemChanged={setLineItemChanged}
                  />
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value="3" sx={{ px: { xs: 0, md: 2 } }}>
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}
                  >
                    <Button
                      variant="contained"
                      color="main"
                      onClick={() => setTimeLogDialogOpen(true)}
                      disabled={
                        !(
                          jobStatus === "In Progress" ||
                          jobStatus === "Completed"
                        )
                      }
                    >
                      Add Time Log
                    </Button>
                  </Box>
                  <DataGridPro
                    editMode="row"
                    pagination
                    pageSizeOptions={[5, 10, 20]}
                   rows={
                  jobStatus === "In Progress" || 
                  jobStatus === "Completed" || 
                  jobStatus === "Ongoing"
                    ? timeLogRows
                    : []
                  }
                    rowHeight={62}
                    columns={columnsTimeLog}
                    getRowId={(row) => row.id}
                    disableRowSelectionOnClick
                    sx={{
                      "& .MuiDataGrid-columnHeaderTitle": {
                        textTransform: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value="4" sx={{ px: { xs: 0, md: 2 } }}>
              <TabContext value={tabBvAValue}>
                <TabList
                  sx={{ mb: 2 }}
                  onChange={handleTabBvAChange}
                  aria-label="simple tabs example"
                >
                  <Tab
                    value="1"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                          color:
                            theme.palette.mode === "dark"
                              ? tabBvAValue === "1"
                                ? "#fff"
                                : "inherit"
                              : "inherit",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Summary
                        </Box>
                      </Box>
                    }
                  />
                  <Tab
                    value="2"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                          color:
                            theme.palette.mode === "dark"
                              ? tabBvAValue === "2"
                                ? "#fff"
                                : "inherit"
                              : "inherit",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Details
                        </Box>
                      </Box>
                    }
                  />
                </TabList>
                <TabPanel value="1" sx={{ p: { xs: 0, md: 2 } }}>
                  <DataGridPro
                    editMode="row"
                    pagination
                    pageSizeOptions={[5, 10, 20]}
                    rows={
                      jobStatus === "In Progress" || jobStatus === "Completed"
                        ? bvaSummaryRows
                        : []
                    }
                    rowHeight={62}
                    columns={columnsBvASummary}
                    getRowId={(row) => row.id}
                    loading={bvaLoading}
                    disableRowSelectionOnClick
                    sx={{
                      "& .MuiDataGrid-columnHeaderTitle": {
                        textTransform: "none",
                      },
                    }}
                  />
                </TabPanel>
                <TabPanel value="2" sx={{ p: { xs: 0, md: 2 } }}>
                  <LineItems
                    entity="jobDetailBvA"
                    job={job}
                    workReq={workReq}
                    rules={rules}
                    setLoading={setLoading}
                    toast={toast}
                    forceEmpty={
                      !(
                        jobStatus === "In Progress" || jobStatus === "Completed"
                      )
                    }
                    LineItemProps={bvaLogic.LineItemProps}
                    LineItemObservationProps={bvaLogic.LineItemObservationProps}
                    LineItemMaterialProps={bvaLogic.LineItemMaterialProps}
                    LineItemLaborProps={bvaLogic.LineItemLaborProps}
                    LineItemSubcontractorProps={bvaLogic.LineItemSubcontractorProps}
                    LineItemMiscCostProps={bvaLogic.LineItemMiscCostProps}
                    setLineItemChanged={setLineItemChanged}
                  />
                </TabPanel>
              </TabContext>
            </TabPanel>
          </TabContext>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
        >
          <Button variant="contained" color="info" onClick={handleCloseJob}>
            Cancel
          </Button>
          <Button variant="contained" color="main" onClick={handleSaveJob}>
            Save
          </Button>
        </DialogActions>
               
      </Dialog>

      {/* Add Time Log Dialog */}
      <AddTimeLog
        open={timeLogDialogOpen}
        handleClose={handleTimeLogDialogClose}
        jobID={job?.jobID}
        employees={distinctEmployees}
        mode={timeLogDialogMode} // new!
        log={editingLog} // new!
        onSuccess={() => {
          handleTimeLogSuccess();
          setEditingLog(null);
          setTimeLogDialogMode("add");
        }}
      />
    </>
  );
};

export default JobDetail;
